// GoogleMaps.jsx
import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import { Autocomplete } from "@react-google-maps/api";

const defaultContainerStyle = {
  width: "100%",
  height: "420px",
  borderRadius: 12,
  overflow: "hidden",
};

const GoogleMaps = ({
  mapsApi = [],
  apiKey,
  containerStyle,
  defaultZoom = 14,
  l,
  type,
  setFilteredMapsApi,
  originalMapsApi
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ["places"], // <— important
  });

  const [activePlaceId, setActivePlaceId] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const searchInputRef = useRef();


  const checkEmpty = (e) => {
    if(e.target.value.trim() === ""){
      setFilteredMapsApi(originalMapsApi)
    };
  };

  const onLoadAutocomplete = (auto) => {
    setAutocomplete(auto);
  };

  const getDistance = (loc1, loc2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth radius in km
    const dLat = toRad(loc2.lat - loc1.lat);
    const dLng = toRad(loc2.lng - loc1.lng);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(loc1.lat)) * Math.cos(toRad(loc2.lat)) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place.geometry) return;

    const location = place.geometry.location.toJSON(); // { lat, lng }

    // Filter or sort your retailers by distance
    const sortedRetailers = originalMapsApi
      .map((retailer) => {
        const rLoc = retailer?.googledata?.geometry?.location;
        if(!rLoc) return { ...retailer, distance: Infinity };
        const distance = getDistance(location, rLoc);
        return { ...retailer, distance };
      })
      .sort((a, b) => a.distance - b.distance);

    setFilteredMapsApi(sortedRetailers.slice(0, 5)); // top 5 nearest
  };

  const markers = useMemo(
    () =>
      (mapsApi || [])
        .map((m) => {
          const g = m?.googledata.geometry?.location;
          if (!g || typeof g.lat !== "number" || typeof g.lng !== "number") {
            return null;
          }
          return {
            place_id: m.googledata.place_id,
            position: { lat: g.lat, lng: g.lng },
            title: m.googledata.formatted_address,
            item: m.googledata,
            title2: m.title,
          };
        })
        .filter(Boolean),
    [mapsApi]
  );

  const center = useMemo(() => {
    if (markers.length > 0) return markers[0].position;
    return { lat: 59.3293, lng: 18.0686 }; // Stockholm fallback
  }, [markers]);

  const onMapLoad = useCallback(
    (map) => {
      if (!markers.length) return;
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((m) => bounds.extend(m.position));
      if (markers.length === 1) {
        const pad = 0.0025;
        const { lat, lng } = markers[0].position;
        bounds.extend({ lat: lat + pad, lng: lng + pad });
        bounds.extend({ lat: lat - pad, lng: lng - pad });
      }
      map.fitBounds(bounds, 48);
    },
    [markers]
  );

  if (loadError) {
    return (
      <div>
        <p>Couldn’t load Google Maps: {String(loadError)}</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {type !== "contact" ? 
      <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
        <input
          ref={searchInputRef}
          onChange={checkEmpty}
          type="text"
          className="form-control mb-2"
          placeholder={l("Sök efter en plats...", "Search for a location...")}
        />
      </Autocomplete> : <></>}
      <GoogleMap
        mapContainerStyle={containerStyle || defaultContainerStyle}
        center={center}
        zoom={defaultZoom}
        onLoad={onMapLoad}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        {markers.map((m) => (
          <Marker
            key={m.place_id}
            position={m.position}
            title={m.title}
            onClick={() => setActivePlaceId(m.place_id)}
          />
        ))}

        {markers.map(
          (m) =>
            activePlaceId === m.place_id && (
              <InfoWindow
                key={`${m.place_id}-info`}
                position={m.position}
                onCloseClick={() => setActivePlaceId(null)}
              >
                <div style={{ maxWidth: 260 }}>
                  <h4 className="text-center">{m.title2}</h4>
                  <strong>{m.item.formatted_address}</strong>
                </div>
              </InfoWindow>
            )
        )}
      </GoogleMap>
    </div>
  );
};

export default GoogleMaps;
